import PropTypes from 'prop-types';
import { MessageCircle, MoreHorizontal } from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './FriendCard.module.css';

/**
 * FriendCard - Displays friend information with online status and activity
 * 
 * @param {Object} friend - Friend data object
 * @param {function} onMessage - Message button click handler
 * @param {function} onMore - More options click handler
 * @param {Object} style - Additional inline styles
 */
export function FriendCard({ friend, onMessage, onMore, style }) {
  return (
    <article className={styles.card} style={style}>
      {/* Avatar with online status */}
      <div className={styles.avatarContainer}>
        <div className={styles.avatar}>
          {friend.name.charAt(0).toUpperCase()}
        </div>
        <span 
          className={`${styles.statusDot} ${friend.isOnline ? styles.online : styles.offline}`}
          aria-label={friend.isOnline ? 'Online' : 'Offline'}
        />
      </div>

      {/* Friend Info */}
      <div className={styles.info}>
        <Stack direction="column" gap={0}>
          <Heading level={3} size="sm" color="primary" className={styles.name}>
            {friend.name}
          </Heading>
          <Text size="xs" color="muted" className={styles.username}>
            {friend.username}
          </Text>
          {friend.isOnline && friend.activity ? (
            <Text size="xs" color="secondary" className={styles.activity}>
              {friend.activity}
            </Text>
          ) : (
            <Text size="xs" color="muted" className={styles.lastSeen}>
              {friend.isOnline ? 'Online' : friend.lastSeen || 'Offline'}
            </Text>
          )}
        </Stack>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <button 
          className={styles.actionButton}
          onClick={onMessage}
          aria-label={`Message ${friend.name}`}
        >
          <MessageCircle size={18} />
        </button>
        <button 
          className={styles.actionButton}
          onClick={onMore}
          aria-label="More options"
        >
          <MoreHorizontal size={18} />
        </button>
      </div>
    </article>
  );
}

FriendCard.propTypes = {
  friend: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    isOnline: PropTypes.bool.isRequired,
    activity: PropTypes.string,
    lastSeen: PropTypes.string,
    avatar: PropTypes.string,
  }).isRequired,
  onMessage: PropTypes.func,
  onMore: PropTypes.func,
  style: PropTypes.object,
};

export default FriendCard;
